// app/(account)/page.tsx
import { Package, Heart, ShoppingBag, CheckCircle, Truck } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { PageContainer } from "../../../components/ui/PageContainer";
import { useClientDashboard } from "@cabukaka/hooks";

export default function AccountDashboard() {
  const navigate = useNavigate();
  const { data, loading, error } = useClientDashboard();

  if (loading) {
    return (
      <PageContainer withContainer>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </PageContainer>
    );
  }

  if (error) {
    return (
      <PageContainer withContainer>
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-600">
          Erreur : {error}
        </div>
      </PageContainer>
    );
  }

  const stats = [
    { 
      icon: ShoppingBag, 
      label: "Commandes", 
      value: data?.totalOrders || 0, 
      color: "bg-blue-100 text-blue-600",
      onClick: () => navigate("/compte/mes-commandes")
    },
    { 
      icon: Heart, 
      label: "Favoris", 
      value: data?.favoritesCount || 0, 
      color: "bg-red-100 text-red-600",
      onClick: () => navigate("/compte/favoris")
    },
    { 
      icon: Package, 
      label: "En attente", 
      value: data?.pendingOrders || 0, 
      color: "bg-orange-100 text-orange-600",
      onClick: () => navigate("/compte/mes-commandes?status=pending")
    },
    { 
      icon: CheckCircle, 
      label: "Livrées", 
      value: data?.deliveredOrders || 0, 
      color: "bg-green-100 text-green-600",
      onClick: () => navigate("/compte/mes-commandes?status=delivered")
    },
  ];

  const getStatusBadge = (status: string) => {
    const statusMap: Record<string, { label: string; className: string }> = {
      pending: { label: "En attente", className: "text-yellow-600 bg-yellow-100" },
      confirmed: { label: "Confirmée", className: "text-blue-600 bg-blue-100" },
      preparing: { label: "En préparation", className: "text-purple-600 bg-purple-100" },
      shipped: { label: "Expédiée", className: "text-indigo-600 bg-indigo-100" },
      delivered: { label: "Livrée", className: "text-green-600 bg-green-100" },
      cancelled: { label: "Annulée", className: "text-red-600 bg-red-100" }
    };
    return statusMap[status] || { label: status, className: "text-gray-600 bg-gray-100" };
  };

  return (
    <PageContainer withContainer className="space-y-6 md:space-y-8">
      {/* Bienvenue */}
      <div className="bg-gradient-to-r from-primary/10 to-transparent rounded-xl p-4 md:p-6">
        <h2 className="text-lg md:text-xl font-bold text-gray-900 mb-1">
          Bonjour 👋
        </h2>
        <p className="text-sm md:text-base text-gray-600">
          Bienvenue dans votre espace personnel
        </p>
      </div>

      {/* Statistiques */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
        {stats.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <button
              key={index}
              onClick={stat.onClick}
              className="bg-white border border-gray-100 rounded-xl p-3 md:p-4 text-left hover:shadow-md transition-shadow"
            >
              <div className={`w-8 h-8 md:w-10 md:h-10 ${stat.color} rounded-lg flex items-center justify-center mb-2 md:mb-3`}>
                <Icon className="w-4 h-4 md:w-5 md:h-5" />
              </div>
              <p className="text-xl md:text-2xl font-bold text-gray-900">{stat.value}</p>
              <p className="text-xs md:text-sm text-gray-500">{stat.label}</p>
            </button>
          );
        })}
      </div>

      {/* Actions rapides */}
      <div className="grid grid-cols-2 gap-3 md:gap-4">
        <button
          onClick={() => navigate("/compte/mes-commandes")}
          className="flex items-center justify-center gap-2 p-3 md:p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors"
        >
          <Package className="w-4 h-4 md:w-5 md:h-5 text-primary" />
          <span className="text-sm md:text-base font-medium">Mes commandes</span>
        </button>
        <button
          onClick={() => navigate("/compte/favoris")}
          className="flex items-center justify-center gap-2 p-3 md:p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors"
        >
          <Heart className="w-4 h-4 md:w-5 md:h-5 text-red-500" />
          <span className="text-sm md:text-base font-medium">Mes favoris</span>
        </button>
      </div>

      {/* Dernières commandes */}
      {data?.recentOrders && data.recentOrders.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base md:text-lg font-semibold text-gray-900">
              Dernières commandes
            </h3>
            <button
              onClick={() => navigate("/compte/mes-commandes")}
              className="text-xs md:text-sm text-primary hover:underline"
            >
              Voir tout
            </button>
          </div>

          <div className="space-y-3">
            {data.recentOrders.map((order) => {
              const status = getStatusBadge(order.status);
              return (
                <div
                  key={order.id}
                  className="bg-white border border-gray-100 rounded-xl p-4 md:p-5"
                >
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                    <div>
                      <p className="font-semibold text-gray-900 text-sm md:text-base">
                        {order.reference}
                      </p>
                      <p className="text-xs md:text-sm text-gray-500 mt-1">
                        {new Date(order.createdAt).toLocaleDateString('fr-FR')}
                      </p>
                    </div>
                    <div className="flex items-center justify-between md:justify-end gap-4">
                      <span className="font-bold text-gray-900 text-sm md:text-base">
                        {order.total.toLocaleString('fr-FR')} FCFA
                      </span>
                      <span className={`text-xs px-2 py-1 rounded-full ${status.className}`}>
                        {status.label}
                      </span>
                      <button
                        onClick={() => navigate(`/compte/mes-commandes/${order.id}`)}
                        className="text-primary text-sm hover:underline"
                      >
                        Détails
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Besoin d'aide */}
      <div className="bg-gray-50 rounded-xl p-4 md:p-6 text-center">
        <Truck className="w-8 h-8 md:w-10 md:h-10 text-gray-400 mx-auto mb-3" />
        <h3 className="font-semibold text-gray-900 mb-1 text-sm md:text-base">
          Besoin d'aide ?
        </h3>
        <p className="text-xs md:text-sm text-gray-500 mb-3">
          Notre service client est à votre disposition
        </p>
        <button
          onClick={() => navigate("/contact")}
          className="text-primary text-sm font-medium hover:underline"
        >
          Nous contacter
        </button>
      </div>
    </PageContainer>
  );
}